/**
 * Formatos de números, porcentajes, duraciones y horas con `Intl` (configuración regional es-PE).
 * Las unidades siempre se muestran junto a cada valor.
 */

import { diaDe, formatoHora } from '../nucleo/dominio/tiempo';

const REGION = 'es-PE';
const cache = new Map<number, Intl.NumberFormat>();

export function numero(v: number, decimales = 0): string {
  let f = cache.get(decimales);
  if (!f) {
    f = new Intl.NumberFormat(REGION, {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
    cache.set(decimales, f);
  }
  return f.format(v);
}

/**
 * Cantidades (autos, metros, promedios de conteos): los enteros se muestran sin decimales
 * («38», no «38.0»); el resto, con `decimales` cifras («15.7»). Se decide después de redondear.
 */
export function cantidad(v: number, decimales = 1): string {
  const factor = 10 ** decimales;
  const redondeado = Math.round(v * factor) / factor;
  return numero(redondeado, Number.isInteger(redondeado) ? 0 : decimales);
}

const MEDIDA = new Intl.NumberFormat(REGION, { maximumFractionDigits: 2 });

/** Medidas en metros con hasta dos decimales y sin ceros sobrantes («0.45», «3.7», «6»). */
export function medida(v: number): string {
  return MEDIDA.format(v);
}

/** Promedios de conteos: un valor positivo que redondeado daría «0» se muestra «< 0.1». */
export function promedioDeConteo(v: number): string {
  return v > 0 && v < 0.05 ? `< ${numero(0.1, 1)}` : cantidad(v, 1);
}

/** Fracción (0–1) → «74.5 %». */
export function porcentaje(fraccion: number, decimales = 1): string {
  return `${numero(fraccion * 100, decimales)} %`;
}

/** Puntos porcentuales para semiamplitudes de porcentajes. */
export function puntosPorcentuales(fraccion: number, decimales = 1): string {
  return `${numero(fraccion * 100, decimales)} pp`;
}

/** Minutos → «45 min» o «2 h 48 min». */
export function duracion(minutos: number): string {
  const m = Math.round(minutos);
  if (Math.abs(m) < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const resto = m - h * 60;
  return resto === 0 ? `${h} h` : `${h} h ${resto} min`;
}

/** Segundos → «45 s» o «2 min 05 s» (traslados cortos). */
export function duracionCorta(segundos: number): string {
  const s = Math.round(segundos);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${String(s - m * 60).padStart(2, '0')} s`;
}

/** Bytes → «850 B», «18 KB», «1.4 MB», «2.1 GB» (múltiplos de 1024, como los navegadores). */
export function bytes(n: number): string {
  const unidades = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < unidades.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 || v >= 10 ? numero(Math.round(v)) : cantidad(v, 1)} ${unidades[i]}`;
}

/** Instante de la simulación → «08:42» o «día 2 · 09:47». */
export function horaSimulada(t: number, conDia = false): string {
  const hora = formatoHora(t);
  return conDia || diaDe(t) > 1 ? `día ${diaDe(t)} · ${hora}` : hora;
}

const fechaCorta = new Intl.DateTimeFormat(REGION, {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'America/Lima',
});

export function fechaHora(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fechaCorta.format(d);
}

const fechaDia = new Intl.DateTimeFormat(REGION, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Lima',
});

/** Fecha de hoy en Lima («28/10/2026»), para el cajetín de la planta exportada. */
export function fechaDeHoy(): string {
  return fechaDia.format(new Date());
}

/** Fecha y hora actuales en ISO 8601 con la zona de Lima (UTC−5, sin horario de verano). */
export function ahoraIso(): string {
  const ahora = new Date(Date.now() - 5 * 3600 * 1000);
  return ahora.toISOString().replace('Z', '-05:00');
}
